import { useContext, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import SoftButton from "components/SoftButton";
import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import { useSelector, useDispatch } from "react-redux";
import { CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import { THEME_COLORS } from "constants/theme";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";
import { TenantContext } from "context/AppProvider/TenantContext";
import SoftTypography from "components/SoftTypography";
import JSZip from "jszip";
import { CONNECTOR_REQUIRED_FILES } from "constants/flowDesigner";
import { extractConnectorZipFileInformation } from "api/ComponentsApi";
import { isValidUuid } from "helpers/versions";
import {
  closeRegisterComponentDialog,
  createComponent,
  setSavingComponentFlagReset,
  setUpdatedComponent
} from "redux/actions/flowDesigner/cxfComponentsActionCreator";
import { Form, Formik } from "formik";
import CxfFormField from "cxfcomponents/FormField";
import FormField from "layouts/Flow Designer/components/FormField";
import defaultConnectorIcon from "assets/images/connector-list-view.svg";
import { EditPictureMenu } from "components/Picture/Edit";
import DeletePropertiesSection from "cxfcomponents/DeletePropertiesSection";
import { uploadFile } from "helpers/common";
import { updateComponent as updateComponentApi } from "api/FlowDesignerApis";

const FileNamesWrapper = styled(SoftTypography)(({ theme }) => ({
  marginTop: "8px",
  lineHeight: "20px",
  backgroundColor: THEME_COLORS[theme.palette?.mode?.toUpperCase()].INPUT_BG,
  padding: "8px",
  borderRadius: "4px"
}));

export const RegisterComponentDialog = ({
  onClose,
  component,
  isReadOnly = false,
  open = false,
  canDelete = false,
  onDelete
}) => {
  const {
    showRegisterComponentDialog,
    savingComponentFlagSuccess,
    saveComponentFailed,
    errorMessage,
    components
  } = useSelector((store) => store?.designer?.cxfComponents);

  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showProperties, setShowProperties] = useState(isReadOnly || Boolean(component));
  const [uploadedComponent, setUploadedComponent] = useState(component || null);
  const [pictureUpload, setPictureUpload] = useState(null);

  const dispatch = useDispatch();
  const { user } = useAuth0();
  const { tenantData } = useContext(TenantContext);
  const isEditMode = Boolean(component && !isReadOnly);

  // Determine if dialog should be open
  const isDialogOpen = isReadOnly || isEditMode ? open : showRegisterComponentDialog;

  const validateConfigFiles = async (file) => {
    if (!file) {
      return "Config file is required.";
    }

    try {
      const zip = await JSZip.loadAsync(file);
      const entries = Object.keys(zip.files);

      if (entries.some((name) => name.includes("/"))) {
        return "ZIP must not contain folders or nested files.";
      }

      const topLevelFiles = entries.filter((name) => !name.includes("/"));
      const missingFiles = CONNECTOR_REQUIRED_FILES.filter((req) => !topLevelFiles.includes(req));

      if (missingFiles.length > 0) {
        return `Missing required files: ${missingFiles.join(", ")}`;
      }

      return null;
    } catch (e) {
      return "Invalid ZIP file.";
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const validationError = await validateConfigFiles(file);
      setError(validationError || "");
      setSelectedFile(validationError ? null : file);
    }
  };

  const handleSubmit = async () => {
    setError("");
    if (submitting || !selectedFile) return;

    setSubmitting(true);

    const configUpload = document.getElementById("componentConfigUpload");

    if (!configUpload?.files?.[0]) {
      setSubmitting(false);
      return;
    }

    const zipResponse = await extractConnectorZipFileInformation(configUpload.files[0]);
    const zipInformation = zipResponse?.resultObject?.schema;
    const readmeContent = zipResponse?.resultObject?.readme || "";

    if (!zipResponse?.isSuccessful || !zipInformation) {
      setError(zipResponse?.message || "Failed to extract connector information");
      setSubmitting(false);
      return;
    }

    const validationErrors = [];

    if (!zipInformation.version) {
      validationErrors.push("Version is missing in schema.json");
    }

    if (!isValidUuid(zipInformation.componentUuid)) {
      validationErrors.push("componentUuid in schema.json is missing or has an invalid UUID format");
    } else {
      const existingUuids = components.map((c) => c.id?.toLowerCase()).filter(Boolean);
      if (existingUuids.includes(zipInformation.componentUuid.toLowerCase())) {
        validationErrors.push("Component UUID already exists. Please change the componentUuid under your schema.json");
      }
    }

    const existingComponentIds = components.map((c) => c.schema?.componentId?.toLowerCase()).filter(Boolean);
    if (!zipInformation.componentId) {
      validationErrors.push("Component ID is missing in schema.json");
    } else if (existingComponentIds.includes(zipInformation.componentId.toLowerCase())) {
      validationErrors.push("Component ID already exists. Please change the componentId under your schema.json");
    }

    if (validationErrors.length > 0) {
      setError(validationErrors);
      setSubmitting(false);
      return;
    }

    const existingNames = components.map((c) => c.name.toLowerCase());
    let resolvedName = zipInformation.name;
    if (existingNames.includes(resolvedName.toLowerCase())) {
      const base = resolvedName.replace(/\s*\(\d+\)$/, "");
      let counter = 1;
      while (existingNames.includes(`${base} (${counter})`.toLowerCase())) counter++;
      resolvedName = `${base} (${counter})`;
    }

    const componentData = {
      component_type: "",
      config_files: "",
      description: zipInformation.description,
      logo: "",
      name: resolvedName,
      parent_component_id: "",
      price: null,
      scope: "internal",
      status: "active",
      tags: zipInformation.tags ? `${zipInformation.tags}` : "",
      tenant_id: user?.stripeId,
      user_name: user?.name,
      industry: "",
      support: zipInformation.support,
      browser_compatibility: "",
      developer: tenantData?.company_name ?? "",
      developer_logo: tenantData?.logo_url ?? "",
      created_by_id: user?.cxfUserId,
      documentation: readmeContent,
      version: zipInformation.version,
      schema: {
        componentUuid: zipInformation.componentUuid,
        developer: zipInformation.developer ?? "",
        componentId: zipInformation.componentId ?? ""
      }
    };

    setUploadedComponent(componentData);

    dispatch(
      createComponent(configUpload, user, componentData, null, (data) => {
        setUploadedComponent((prev) => ({
          ...prev,
          ...data
        }));
      })
    );
  };

  const closeForm = () => {
    setSelectedFile(null);
    setError("");
    setPictureUpload(null);
    setSubmitting(false);

    setTimeout(() => {
      if (!isEditMode) {
        setShowProperties(false);
        setUploadedComponent(null);
      }
    }, 750);

    if (!isReadOnly && !isEditMode) {
      dispatch(closeRegisterComponentDialog());
    }

    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    if (savingComponentFlagSuccess && !isReadOnly && !isEditMode) {
      toast.success("Connector successfully created");
      setSubmitting(false);
      dispatch(setSavingComponentFlagReset());
      setShowProperties(true);
    }
  }, [savingComponentFlagSuccess, isReadOnly, isEditMode]);

  useEffect(() => {
    if (saveComponentFailed) {
      setSubmitting(false);
      if (errorMessage)
        setError(errorMessage);
      else setError("Failed to upload connector. Please check that your connector schema.json contains the expected properties and that their values have the expected data type.");
    }
  }, [saveComponentFailed]);

  useEffect(() => {
    if (!isReadOnly && !isEditMode && !showRegisterComponentDialog) {
      setSelectedFile(null);
      setError("");
      setSubmitting(false);
      setPictureUpload(null);
      setTimeout(() => {
        setShowProperties(false);
        setUploadedComponent(null);
      }, 750);
    }
  }, [showRegisterComponentDialog, isReadOnly, isEditMode]);

  useEffect(() => {
    if (isReadOnly || isEditMode) {
      setShowProperties(true);
      setUploadedComponent(component || null);
      setPictureUpload(null);
    }
  }, [isReadOnly, isEditMode, component]);

  const handleUpdateProperties = async (values) => {
    const targetComponentId = displayComponent?.id;

    if (submitting || !targetComponentId) return;

    if (!values?.name?.trim()) {
      setError("Connector name is required.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const input = {
        name: values.name.trim(),
        description: values.description?.trim() || "",
        tags: Array.isArray(values.tags) ? values.tags.join(",") : values.tags || ""
      };

      if (pictureUpload) {
        const result = await uploadFile(user, pictureUpload, "connectors", targetComponentId, true);
        input.logo = result?.Location;
      } else if (pictureUpload === "") {
        input.logo = "";
      }

      const updateResponse = await updateComponentApi(user?.stripeId, targetComponentId, input);

      if (!updateResponse?.isSuccessful) {
        setError(updateResponse?.message || "Failed to update connector.");
        setSubmitting(false);
        return;
      }

      const updatedComponent = {
        ...displayComponent,
        ...input,
        id: targetComponentId,
        updated_date_time: new Date().toISOString()
      };

      dispatch(setUpdatedComponent(updatedComponent));
      setUploadedComponent(updatedComponent);
      toast.success("Connector properties updated.");
      setSubmitting(false);
      closeForm();
    } catch (updateError) {
      console.error(updateError);
      setError("Failed to update connector.");
      setSubmitting(false);
    }
  };

  const displayComponent = uploadedComponent || component;
  const showUploadView = !showProperties && !isReadOnly && !isEditMode;
  const showPropertiesView = showProperties || isReadOnly || isEditMode;
  
  return (
    <Dialog
      open={isDialogOpen}
      onClose={closeForm}
      fullWidth
      maxWidth={showPropertiesView ? "csm" : "sm"}
    >
      <DialogTitle
        sx={{
          padding: "32px 48px 0",
          marginBottom: showPropertiesView ? "20px" : "0"
        }}
        className="white-to-dark"
      >
        {showUploadView ? "Upload Connector" : "Connector Properties"}
      </DialogTitle>

      <DialogContent sx={{ padding: showPropertiesView ? "0 48px 24px" : "24px 48px" }}>
        {showUploadView ? (
          <SoftBox sx={{ paddingTop: "24px" }}>
            <SoftTypography variant="caption" fontWeight="bold" mb={1}>
              Upload Package <span style={{ color: "rgb(255, 102, 124)" }}>*</span>
            </SoftTypography>
            <SoftInput
              id="componentConfigUpload"
              type="file"
              onChange={handleFileChange}
              error={!!error}
              inputProps={{ accept: ".zip" }}
              size="medium"
            />
            {selectedFile && (
              <FileNamesWrapper variant="caption" color="dark">
                {selectedFile.name}
              </FileNamesWrapper>
            )}
            {error && (
              <SoftBox mt={1}>
                {Array.isArray(error) ? (
                  error.map((err, idx) => (
                    <SoftTypography key={idx} variant="caption" color="error" sx={{ display: "block", mt: 0.5, fontSize: "14px" }}>
                      • {err}
                    </SoftTypography>
                  ))
                ) : (
                  <SoftTypography variant="caption" color="error" sx={{ display: "block", fontSize: "14px" }}>
                    {error}
                  </SoftTypography>
                )}
              </SoftBox>
            )}
          </SoftBox>
        ) : (
          <>
            <Formik
              initialValues={{
                name: displayComponent?.name ?? "",
                description: displayComponent?.description ?? "",
                tags: displayComponent?.tags?.length ? displayComponent?.tags?.split(",") : [],
                logo: displayComponent?.logo ?? ""
              }}
              onSubmit={handleUpdateProperties}
              enableReinitialize
            >
              {({ values }) => (
                <Form id="register-component-properties-form">
                  <SoftBox
                    className="border-top"
                    sx={{
                      alignItems: "flex-start",
                      display: "flex",
                      paddingTop: "20px"
                    }}
                  >
                    <EditPictureMenu
                      pictureUpload={pictureUpload}
                      setPictureUpload={setPictureUpload}
                      value={displayComponent?.logo}
                      params={displayComponent?.updated_date_time ?? ""}
                      isReadOnlyAccess={isReadOnly}
                      defaultPicture={defaultConnectorIcon}
                      isDisabled={isReadOnly}
                    />
                    <SoftBox
                      sx={{
                        flexGrow: 1,
                        transform: "translateY(-12px)"
                      }}
                    >
                      <CxfFormField type="text" label="name" name="name" disabled={isReadOnly} />
                      <CxfFormField
                        type="text"
                        label="description"
                        name="description"
                        disabled={isReadOnly}
                        isMultiline
                        rows={2}
                      />
                    </SoftBox>
                  </SoftBox>
                  <SoftBox
                    className="border-top"
                    sx={{
                      paddingTop: "20px"
                    }}
                  >
                    <FormField
                      type="tag"
                      label="tags"
                      name="tags"
                      disabled={isReadOnly}
                      placeholder={values.tags.length === 0 ? "No tags" : " "}
                    />
                  </SoftBox>
                  {error && (
                    <SoftBox mt={2}>
                      {Array.isArray(error) ? (
                        error.map((err, idx) => (
                          <SoftTypography key={idx} variant="caption" color="error" sx={{ display: "block", mt: 0.5, fontSize: "14px" }}>
                            • {err}
                          </SoftTypography>
                        ))
                      ) : (
                        <SoftTypography variant="caption" color="error" sx={{ display: "block", fontSize: "14px" }}>
                          {error}
                        </SoftTypography>
                      )}
                    </SoftBox>
                  )}
                </Form>
              )}
            </Formik>
            {displayComponent?.schema && (
              <SoftBox
                className="border-top"
                sx={{
                  paddingTop: "20px"
                }}
              >
                <SoftBox sx={{ marginBottom: "16px" }}>
                  <SoftTypography variant="caption" fontWeight="bold" mb={1}>
                    Developer
                  </SoftTypography>
                  <SoftInput
                    value={displayComponent.schema.developer || "N/A"}
                    disabled
                    size="medium"
                  />
                </SoftBox>
                <SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" mb={1}>
                    Component ID
                  </SoftTypography>
                  <SoftInput
                    value={displayComponent.schema.componentId || "N/A"}
                    disabled
                    size="medium"
                  />
                </SoftBox>
              </SoftBox>
            )}
          </>
        )}
      </DialogContent>

      {showProperties && component && canDelete && onDelete && (
        <SoftBox sx={{ px: { xs: 3, sm: 6 }, pb: 2 }}>
          <DeletePropertiesSection open={isDialogOpen} resourceName="connector" onDelete={onDelete} disabled={submitting} />
        </SoftBox>
      )}
      <DialogActions sx={{ padding: "0 48px 32px" }}>
        {showUploadView ? (
          <>
            <SoftButton variant="outlined" color="info" onClick={closeForm}>
              Cancel
            </SoftButton>
            <SoftButton
              color="primary"
              onClick={handleSubmit}
              disabled={!selectedFile || !!error || submitting}
            >
              {submitting ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Upload"}
            </SoftButton>
          </>
        ) : (
          <>
            <SoftButton
              onClick={closeForm}
              variant="outlined"
              color="primary"
            >
              {isReadOnly ? "Close" : "Cancel"}
            </SoftButton>
            {!isReadOnly && (
              <SoftButton color="primary" onClick={() => document.getElementById("register-component-properties-form")?.requestSubmit()} disabled={submitting}>
                {submitting ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Save"}
              </SoftButton>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Backward compatibility wrapper
export const ViewConnectorProperties = ({ open, onClose, component }) => {
  return (
    <RegisterComponentDialog
      open={open}
      onClose={onClose}
      component={component}
      isReadOnly={true}
    />
  );
};
